import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Maximize2, X } from "lucide-react";
import { verticalMascots } from "@/data/genieMascots";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/**
 * Internal demo — "One Help Genie Loading Screen".
 *
 * Throwaway, fully self-contained mockup (mirrors /internal/capture-demo) so we
 * can FEEL a few takes on ONE standardised, on-brand loading state before we
 * replace the three inconsistent loaders that exist today (GenieLoader,
 * LoadingOverlay, GenieCreationLoader).
 *
 * The hero of every concept is the Genie mascot itself — the big character
 * rising on its smoke tail out of the lamp — summoned out of brand "genie mist".
 * Light surface, brand gradient + the mascots only, no banned colours, no center
 * dialogs. Whichever concept wins becomes a single <GenieLoader> rendered at
 * three sizes: full-screen overlay, page block, and inline.
 */

const BRAND = "#4E9CFF";
const SECONDARY = "#FF4DB8";
const GRADIENT = `linear-gradient(90deg, ${BRAND} 0%, ${SECONDARY} 100%)`;

/** The mascots are the whole point — the big characters summoned from the lamp. */
const MASCOTS = Object.values(verticalMascots);

const LOADING_MESSAGES = [
  "Summoning your Genie…",
  "Polishing the lamp…",
  "Gathering the magic…",
  "Almost there…",
];

/* ─────────────────────────────────────────────────────────────────────────
   Shared atmosphere
   ───────────────────────────────────────────────────────────────────────── */

/** Smoke that billows UP from the lamp at the base — concentrated low and
 *  centered, drifting upward and clearing, so the mascot reads as conjured out
 *  of the plume rather than just sitting there. Brand blue → magenta only. */
const RisingSmoke = ({ scale = 1 }: { scale?: number }) => {
  const puffs = [
    { x: -34, size: 150, color: BRAND, peak: 0.45, dur: 5, delay: 0 },
    { x: 30, size: 170, color: SECONDARY, peak: 0.4, dur: 5.6, delay: 0.7 },
    { x: -6, size: 200, color: "#FFFFFF", peak: 0.5, dur: 4.6, delay: 0.3 },
    { x: 48, size: 140, color: BRAND, peak: 0.38, dur: 6, delay: 1.3 },
    { x: -52, size: 150, color: SECONDARY, peak: 0.36, dur: 5.4, delay: 1 },
  ];

  return (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-[62%] overflow-hidden">
      {puffs.map((p, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, p.peak, 0],
            y: [40 * scale, -120 * scale],
            scale: [0.6 * scale, 1.5 * scale],
            x: [p.x * 0.4 * scale, p.x * scale],
          }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: "easeOut" }}
          className="absolute left-1/2 bottom-0 rounded-full"
          style={{
            width: p.size * scale,
            height: p.size * scale,
            marginLeft: (-p.size * scale) / 2,
            background: `radial-gradient(circle, ${p.color}66 0%, ${p.color}22 42%, transparent 70%)`,
            filter: `blur(${28 * scale}px)`,
          }}
        />
      ))}
    </div>
  );
};

/** A soft brand halo behind the mascot — blurred, never a solid blob. */
const Halo = ({ size = 320 }: { size?: number }) => (
  <motion.div
    aria-hidden
    className="absolute rounded-full"
    style={{
      width: size,
      height: size,
      background: `radial-gradient(circle, ${BRAND}33 0%, ${SECONDARY}1f 45%, transparent 70%)`,
      filter: "blur(22px)",
    }}
    animate={{ scale: [1, 1.1, 1], opacity: [0.7, 1, 0.7] }}
    transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
  />
);

/** Cycling caption + three breathing dots — an interesting wait, not a label. */
const CyclingCaption = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % LOADING_MESSAGES.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="mt-4 flex flex-col items-center">
      <div className="relative h-6 w-[240px]">
        <AnimatePresence mode="wait">
          <motion.p
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 text-center text-[15px] font-medium text-[#475569]"
          >
            {LOADING_MESSAGES[i]}
          </motion.p>
        </AnimatePresence>
      </div>
      <div className="mt-3 flex gap-1.5">
        {[0, 1, 2].map((d) => (
          <motion.span
            key={d}
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: GRADIENT }}
            animate={{ opacity: [0.25, 1, 0.25], scale: [0.8, 1.15, 0.8] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: d * 0.18, ease: "easeInOut" }}
          />
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Concept 1 — Summoning Carousel  (conjure one mascot after another)
   ───────────────────────────────────────────────────────────────────────── */

const SummoningCarousel = ({ h }: { h: number }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % MASCOTS.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center">
      <RisingSmoke scale={h / 280} />
      <div className="relative flex flex-col items-center">
        <div className="relative flex items-end justify-center" style={{ height: h }}>
          <Halo size={h * 0.95} />
          <AnimatePresence mode="popLayout">
            <motion.img
              key={i}
              src={MASCOTS[i]}
              alt=""
              className="relative object-contain"
              style={{ height: h, filter: `drop-shadow(0 18px 34px ${BRAND}40)` }}
              initial={{ opacity: 0, y: 34, scale: 0.9, filter: "blur(14px)" }}
              animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -26, scale: 1.05, filter: "blur(14px)" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            />
          </AnimatePresence>
        </div>
        <CyclingCaption />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Concept 2 — Hero Summon  (one mascot, floating, rising from the lamp smoke)
   ───────────────────────────────────────────────────────────────────────── */

const HeroSummon = ({ h }: { h: number }) => (
  <div className="relative flex h-full w-full flex-col items-center justify-center">
    <RisingSmoke scale={h / 280} />
    <div className="relative flex flex-col items-center">
      <div className="relative flex items-end justify-center" style={{ height: h }}>
        <Halo size={h * 0.95} />
        <motion.img
          src={MASCOTS[8] /* trades */}
          alt=""
          className="relative object-contain"
          style={{ height: h, filter: `drop-shadow(0 18px 34px ${BRAND}40)` }}
          animate={{ y: [0, -12, 0], rotate: [-1.5, 1.5, -1.5] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      <CyclingCaption />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
   Concept 3 — Halo Hero  (big mascot + rotating brand ring + orbiting sparkles)
   ───────────────────────────────────────────────────────────────────────── */

const GradientRing = ({ id, size }: { id: string; size: number }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    className="absolute"
    animate={{ rotate: 360 }}
    transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
  >
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={BRAND} />
        <stop offset="100%" stopColor={SECONDARY} />
      </linearGradient>
    </defs>
    <circle
      cx="50"
      cy="50"
      r="46"
      fill="none"
      stroke={`url(#${id})`}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeDasharray="150 289"
      opacity="0.55"
    />
  </motion.svg>
);

const HaloHero = ({ h, id }: { h: number; id: string }) => {
  const ringSize = h * 1.04;
  const orbiters = [0, 120, 240];
  const radius = ringSize / 2;
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center">
      <RisingSmoke scale={h / 280} />
      <div className="relative flex flex-col items-center">
        <div className="relative flex items-center justify-center" style={{ height: h, width: ringSize }}>
          <Halo size={h * 0.9} />
          <GradientRing id={id} size={ringSize} />
          <motion.div
            className="absolute"
            style={{ height: ringSize, width: ringSize }}
            animate={{ rotate: 360 }}
            transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
          >
            {orbiters.map((angle, k) => {
              const rad = (angle * Math.PI) / 180;
              return (
                <motion.span
                  key={k}
                  className="absolute"
                  style={{
                    top: "50%",
                    left: "50%",
                    width: 22,
                    height: 22,
                    marginLeft: -11,
                    marginTop: -11,
                    x: Math.cos(rad) * radius,
                    y: Math.sin(rad) * radius,
                  }}
                  animate={{ scale: [0.7, 1.1, 0.7], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2.2, repeat: Infinity, delay: k * 0.4, ease: "easeInOut" }}
                >
                  <GenieIcon name="sparkles" size={22} />
                </motion.span>
              );
            })}
          </motion.div>
          <motion.img
            src={MASCOTS[5] /* marine */}
            alt=""
            className="relative object-contain"
            style={{ height: h, filter: `drop-shadow(0 18px 34px ${BRAND}40)` }}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <CyclingCaption />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Concept registry
   ───────────────────────────────────────────────────────────────────────── */

type ConceptKey = "carousel" | "hero" | "halo";

const CONCEPTS: {
  key: ConceptKey;
  name: string;
  blurb: string;
  render: (h: number, ctx: string) => JSX.Element;
}[] = [
  {
    key: "carousel",
    name: "Summoning Carousel",
    blurb: "Conjures one Genie after another out of the smoke — blur-rises in, holds, dissolves into the next. Shows the whole cast. My pick.",
    render: (h) => <SummoningCarousel h={h} />,
  },
  {
    key: "hero",
    name: "Hero Summon",
    blurb: "One big Genie floating gently, rising out of the lamp smoke. Calm, focused — the simplest standardised loader.",
    render: (h) => <HeroSummon h={h} />,
  },
  {
    key: "halo",
    name: "Halo Hero",
    blurb: "A big Genie framed by a slow brand-gradient ring with orbiting sparkles. The most decorated take.",
    render: (h, ctx) => <HaloHero h={h} id={`ring-${ctx}`} />,
  },
];

/* ─────────────────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────────────────── */

const LoadingDemo = () => {
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState<ConceptKey | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setFullscreen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const active = CONCEPTS.find((c) => c.key === fullscreen);

  return (
    <div className="relative min-h-dvh w-full overflow-x-hidden bg-[#F9FAFC]">
      <Helmet>
        <title>Loading Screen · Internal Demo</title>
      </Helmet>

      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E5EAF0] bg-white/70 px-3 py-1.5 text-xs font-medium text-[#64748B] backdrop-blur transition-colors hover:bg-white hover:text-[#222B45]"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="rounded-full border border-[#E5EAF0] bg-white px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#64748B] shadow-sm">
          Internal demo · Loading Screen
        </div>
        <div className="w-[72px]" />
      </div>

      {/* Intro */}
      <div className="mx-auto max-w-[1100px] px-6 pt-4 text-center">
        <h1 className="text-3xl font-bold text-[#222B45] md:text-4xl">One loading screen, made magical</h1>
        <p className="mx-auto mt-3 max-w-[660px] text-[15px] leading-relaxed text-[#64748B]">
          The Genie mascots take centre stage — each one summoned out of the lamp smoke while we work.
          Whichever take you love becomes the one loader used everywhere, at full-screen, block, and
          inline sizes.
        </p>
      </div>

      {/* Concept grid */}
      <div className="mx-auto grid max-w-[1100px] grid-cols-1 gap-6 px-6 py-10 md:grid-cols-2">
        {CONCEPTS.map((c) => (
          <div
            key={c.key}
            className="overflow-hidden rounded-3xl border border-[#E5EAF0] bg-white shadow-[0_20px_50px_-24px_rgba(15,23,42,0.25)]"
          >
            {/* Stage */}
            <div className="relative h-80 overflow-hidden border-b border-[#EEF2F7] bg-[#FCFDFF]">
              {c.render(230, c.key)}
            </div>
            {/* Meta */}
            <div className="flex items-start justify-between gap-4 p-5">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-[#222B45]">{c.name}</h3>
                <p className="mt-1 text-[13px] leading-snug text-[#64748B]">{c.blurb}</p>
              </div>
              <button
                onClick={() => setFullscreen(c.key)}
                className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold text-white shadow-md transition-transform hover:scale-105"
                style={{ background: GRADIENT, boxShadow: `0 6px 16px -6px ${BRAND}80` }}
              >
                <Maximize2 size={13} /> Full screen
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Rollout note */}
      <div className="mx-auto max-w-[1100px] px-6 pb-16">
        <div className="rounded-2xl border border-[#E5EAF0] bg-white p-5">
          <p className="text-[13px] leading-relaxed text-[#64748B]">
            <span className="font-semibold text-[#222B45]">Next:</span> pick one and it replaces the
            three loaders that exist today (<span className="text-[#475569]">GenieLoader</span>,{" "}
            <span className="text-[#475569]">LoadingOverlay</span>,{" "}
            <span className="text-[#475569]">GenieCreationLoader</span>) with a single{" "}
            <span className="text-[#475569]">GenieLoader</span> rendered at three sizes — full-screen
            route loads, in-page blocks, and small inline waits.
          </p>
        </div>
      </div>

      {/* Full-screen preview */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-[#F9FAFC]"
          >
            <button
              onClick={() => setFullscreen(null)}
              className="absolute right-5 top-5 z-10 inline-flex items-center gap-1.5 rounded-full border border-[#E5EAF0] bg-white/80 px-3 py-1.5 text-xs font-medium text-[#64748B] backdrop-blur transition-colors hover:bg-white hover:text-[#222B45]"
            >
              <X size={14} /> Close
            </button>
            <div className="absolute left-1/2 top-5 z-10 -translate-x-1/2 rounded-full border border-[#E5EAF0] bg-white px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#64748B] shadow-sm">
              {active.name}
            </div>
            <div className="h-full w-full">{active.render(440, `fs-${active.key}`)}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoadingDemo;
